// Grafica.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import ApiCall from '../utils/apis';
import '../assets/css/Grafica.css';

const VERDE = '#2dce89';
const ROJO = '#f5365c';
const AZUL = '#11cdef';

const money = v => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const PRICE_DAYS = 7;   // la gráfica de precio muestra solo la última semana (velas de 1 h)

const Grafica = (props) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchTrades = useCallback(async () => {
        if (!props.botId) return;
        setLoading(true);
        setError('');
        try {
            const res = await ApiCall({ call: `trades?botId=${props.botId}&days=120&priceDays=${PRICE_DAYS}`, method: 'GET' });
            if (res && Array.isArray(res.price)) {
                setData(res);
            } else if (res && res.error) {
                setError(res.error);
                setData(null);
            } else {
                setError('Error de conexión con el servidor');
                setData(null);
            }
        } catch (err) {
            setError('Error de conexión con el servidor');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [props.botId]);

    // Limpiar antes de cambiar de activo: evita mostrar cifras del simbolo anterior
    useEffect(() => { setData(null); }, [props.botId]);

    useEffect(() => { fetchTrades(); }, [fetchTrades]);

    // Auto-refresh cada 30 segundos, igual que el visor de logs
    useEffect(() => {
        const interval = setInterval(fetchTrades, 30000);
        return () => clearInterval(interval);
    }, [fetchTrades]);

    if (error) {
        return <div className="grafica"><div className="grafica-error">❌ {error}</div></div>;
    }
    if (!data) {
        return <div className="grafica"><div className="grafica-loading">⏳ Cargando gráfica...</div></div>;
    }

    const { price = [], trades = [], closed = [], summary = {} } = data;
    // La gráfica de precio es de la última semana; el resumen y el P&L siguen con todo el historial
    const desde = Date.now() - PRICE_DAYS * 86400000;
    const semana = trades.filter(t => t.ts >= desde);
    const buys = semana.filter(t => t.side === 'buy');
    const sells = semana.filter(t => t.side === 'sell');

    // Las velas pueden ir atrasadas; cada operacion es un precio real en su momento,
    // asi la linea llega hasta las compras/ventas y los marcadores quedan sobre ella
    const puntos = [
        ...price.filter(p => p.ts >= desde).map(p => ({ x: new Date(p.ts), y: p.c })),
        ...semana.map(t => ({ x: new Date(t.ts), y: t.price })),
    ].sort((a, b) => a.x - b.x);

    // ── Gráfica 1: precio + marcadores de compra/venta ──────────────────────
    const precioData = {
        datasets: [
            {
                label: 'Precio', type: 'line', order: 3,
                data: puntos,
                borderColor: AZUL, backgroundColor: 'rgba(17,205,239,0.10)',
                borderWidth: 2, pointRadius: 0, pointHitRadius: 6, fill: true, lineTension: 0.2,
            },
            {
                label: 'Compras', type: 'line', order: 1,
                data: buys.map(t => ({ x: new Date(t.ts), y: t.price })),
                showLine: false, fill: false,
                pointStyle: 'triangle', pointRadius: 6, pointHoverRadius: 9,
                backgroundColor: VERDE, borderColor: VERDE,
            },
            {
                label: 'Ventas', type: 'line', order: 2,
                data: sells.map(t => ({ x: new Date(t.ts), y: t.price })),
                showLine: false, fill: false,
                pointStyle: 'rectRot', pointRadius: 6, pointHoverRadius: 9,
                backgroundColor: ROJO, borderColor: ROJO,
            },
        ],
    };

    const precioOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: 'top' },
        hover: { mode: 'nearest', intersect: true },
        tooltips: {
            mode: 'nearest', intersect: true,
            callbacks: {
                label: (item, d) => `${d.datasets[item.datasetIndex].label}: ${money(item.yLabel)}`,
            },
        },
        scales: {
            xAxes: [{
                type: 'time',
                distribution: 'linear',
                time: {
                    unit: 'day',
                    min: new Date(desde),
                    max: new Date(),
                    tooltipFormat: 'DD MMM YYYY HH:mm',
                    displayFormats: { day: 'DD MMM' },
                },
                gridLines: { display: false },
                ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
            }],
            yAxes: [{
                ticks: { callback: v => money(v) },
                gridLines: { color: 'rgba(0,0,0,0.05)' },
            }],
        },
    };

    // ── Gráfica 2: P&L realizado por operación cerrada ──────────────────────
    const pnlData = {
        labels: closed.map((c, i) => `#${i + 1}`),
        datasets: [{
            label: 'P&L realizado (USDT)',
            data: closed.map(c => c.pnl),
            // chart.js 2.x: un array de colores da color por barra
            backgroundColor: closed.map(c => c.pnl >= 0 ? 'rgba(45,206,137,0.85)' : 'rgba(245,54,92,0.85)'),
            borderColor: closed.map(c => c.pnl >= 0 ? VERDE : ROJO),
            hoverBackgroundColor: closed.map(c => c.pnl >= 0 ? VERDE : ROJO),
            borderWidth: 1,
            maxBarThickness: 30,
        }],
    };

    const pnlOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
            callbacks: {
                title: items => closed[items[0].index].t,
                label: item => {
                    const c = closed[item.index];
                    const lineas = [`P&L: ${money(c.pnl)}`];
                    if (c.pnlPct !== null && c.pnlPct !== undefined) lineas.push(`${c.pnlPct.toFixed(2)}%`);
                    if (c.cost && c.income) lineas.push(`Costo: ${money(c.cost)} / Ingreso: ${money(c.income)}`);
                    if (c.reason) lineas.push(`Motivo: ${c.reason}`);
                    return lineas;
                },
            },
        },
        scales: {
            xAxes: [{ gridLines: { display: false }, ticks: { maxRotation: 0 } }],
            yAxes: [{
                ticks: { callback: v => money(v) },
                gridLines: { zeroLineColor: '#8898aa', zeroLineWidth: 2, color: 'rgba(0,0,0,0.05)' },
            }],
        },
    };

    const winRate = summary.count ? Math.round((summary.wins / summary.count) * 100) : 0;
    const totalClass = (summary.totalPnl || 0) >= 0 ? 'grafica-pos' : 'grafica-neg';

    return (
        <div className="grafica">
            <div className="grafica-toolbar">
                <h3 className="grafica-title">📈 Gráfica — {props.symbol}</h3>
                <div className="grafica-toolbar-right">
                    <span className="grafica-hint">Desde el arranque del bot</span>
                    <button className="grafica-btn" onClick={fetchTrades} disabled={loading} title="Recargar">
                        {loading ? '⏳' : '🔄'} Recargar
                    </button>
                </div>
            </div>

            <div className="grafica-stats">
                <div className="grafica-stat">
                    <span className="grafica-stat-label">P&L total</span>
                    <span className={`grafica-stat-value ${totalClass}`}>{money(summary.totalPnl || 0)}</span>
                </div>
                <div className="grafica-stat">
                    <span className="grafica-stat-label">Operaciones cerradas</span>
                    <span className="grafica-stat-value">{summary.count || 0}</span>
                </div>
                <div className="grafica-stat">
                    <span className="grafica-stat-label">Ganadas / Perdidas</span>
                    <span className="grafica-stat-value">
                        <span className="grafica-pos">{summary.wins || 0}</span>
                        {' / '}
                        <span className="grafica-neg">{summary.losses || 0}</span>
                    </span>
                </div>
                <div className="grafica-stat">
                    <span className="grafica-stat-label">Acierto</span>
                    <span className="grafica-stat-value">{winRate}%</span>
                </div>
            </div>

            <div className="grafica-card">
                <h4 className="grafica-card-title">Precio y operaciones · última semana</h4>
                {puntos.length > 0 ? (
                    <div className="grafica-chart">
                        {/* key fuerza instancia nueva al cambiar de activo: react-chartjs-2 2.x mergea data */}
                        <Line key={`precio-${props.botId}`} data={precioData} options={precioOptions} />
                    </div>
                ) : (
                    <div className="grafica-empty">📭 Sin serie de precios disponible</div>
                )}
            </div>

            <div className="grafica-card">
                <h4 className="grafica-card-title">Ganancia / pérdida por operación cerrada</h4>
                {closed.length > 0 ? (
                    <div className="grafica-chart">
                        <Bar key={`pnl-${props.botId}`} data={pnlData} options={pnlOptions} />
                    </div>
                ) : (
                    <div className="grafica-empty">📭 Sin operaciones cerradas desde el arranque</div>
                )}
            </div>
        </div>
    );
};

export default Grafica;
